import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileText } from 'lucide-react';
import { StatusBadge } from '../common/StatusBadge';
import { formatDuration } from './JobCard';
import ArtifactViewer from './ArtifactViewer';
import type { JobRun } from '../../types/jobs';

interface JobRunsTableProps {
  runs: JobRun[];
  jobId?: string;
}

function formatTokens(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

const SUMMARY_TRUNCATE = 60;

export default function JobRunsTable({ runs, jobId }: JobRunsTableProps) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [artifactRun, setArtifactRun] = useState<JobRun | null>(null);

  const sorted = [...runs].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-bg-tertiary text-text-secondary text-left">
            <th className="px-3 py-2 font-medium">Started At</th>
            <th className="px-3 py-2 font-medium">Duration</th>
            <th className="px-3 py-2 font-medium">Tokens (in/out)</th>
            <th className="px-3 py-2 font-medium">Cost</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Summary</th>
            {jobId && <th className="px-3 py-2 font-medium w-10"></th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((run, idx) => {
            const summary = run.result_summary ?? run.error ?? '';
            const isLong = summary.length > SUMMARY_TRUNCATE;
            const isExpanded = expandedRow === run.id;
            const displaySummary =
              isLong && !isExpanded ? `${summary.slice(0, SUMMARY_TRUNCATE)}...` : summary;

            return (
              <tr
                key={run.id}
                className={idx % 2 === 0 ? 'bg-bg-secondary' : 'bg-bg-secondary/50'}
              >
                <td className="px-3 py-2 text-text-primary whitespace-nowrap">
                  {format(parseISO(run.started_at), 'MMM d, h:mm a')}
                </td>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                  {run.duration_ms != null ? formatDuration(run.duration_ms) : '--'}
                </td>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                  {formatTokens(run.input_tokens)} / {formatTokens(run.output_tokens)}
                </td>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                  ${run.cost_usd.toFixed(2)}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={run.status} size="sm" />
                </td>
                <td className="px-3 py-2 text-text-muted max-w-xs">
                  {isLong ? (
                    <button
                      className="text-left hover:text-text-secondary transition-colors"
                      onClick={() => setExpandedRow(isExpanded ? null : run.id)}
                    >
                      {displaySummary}
                    </button>
                  ) : (
                    <span>{displaySummary}</span>
                  )}
                </td>
                {jobId && (
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setArtifactRun(run)}
                      className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-accent-purple-light transition-colors"
                      title="View full output"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {artifactRun && jobId && (
        <ArtifactViewer
          run={artifactRun}
          jobId={jobId}
          onClose={() => setArtifactRun(null)}
        />
      )}
    </div>
  );
}
